import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { RefreshCw, Search, Shield, Trash2, User, MessageSquare, Ban, X, AlertTriangle } from 'lucide-react';
import ExportButtons from './ExportButtons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const IPLogsManager = ({ highlightUserId, onHighlightComplete }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUserId, setFilterUserId] = useState('');
  const [filterConversationId, setFilterConversationId] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [banDuration, setBanDuration] = useState('1_week');
  const [banReason, setBanReason] = useState('');
  const [banStatuses, setBanStatuses] = useState({});
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(90);
  const { toast } = useToast();

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Auto-scroll and highlight when logs are loaded and highlightUserId prop is set
  useEffect(() => {
    if (highlightUserId && logs.length > 0) {
      console.log('Highlighting user:', highlightUserId);
      const timer = setTimeout(() => {
        const rowElement = document.getElementById(`user-row-${highlightUserId}`);
        if (rowElement) {
          // Check if element is below viewport
          const rect = rowElement.getBoundingClientRect();
          const isBelow = rect.top > window.innerHeight;
          
          if (isBelow) {
            // Show scroll indicator briefly
            toast({
              title: '⬇️ Scrolling to user...',
              description: 'Located in IP logs below',
              duration: 1500
            });
          }
          
          // Smooth scroll to the element
          rowElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          
          // Remove highlight after 2 seconds
          setTimeout(() => {
            if (onHighlightComplete) {
              onHighlightComplete();
            }
          }, 2000);
        } else {
          toast({
            title: 'User not found',
            description: 'The user might not have registered an IP yet.',
            variant: 'default'
          });
          if (onHighlightComplete) {
            onHighlightComplete();
          }
        }
      }, 300); // Small delay to ensure DOM is ready
      
      return () => clearTimeout(timer);
    }
  }, [highlightUserId, logs, toast, onHighlightComplete]);

  useEffect(() => {
    fetchLogs();
  }, [page, filterUserId, filterConversationId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let url = `${API_BASE}/api/admin/ip-logs?page=${page}&limit=50`;
      if (filterUserId) url += `&user_id=${filterUserId}`;
      if (filterConversationId) url += `&conversation_id=${filterConversationId}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Show ALL users including admins
        setLogs(data.logs || []);
        setTotalPages(data.pages || 1);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch IP logs',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching IP logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch IP logs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const openCleanupDialog = (days) => {
    setCleanupDays(days);
    setCleanupDialogOpen(true);
  };

  const handleCleanup = async () => {
    setCleanupDialogOpen(false);
    const days = cleanupDays;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/ip-logs/cleanup?days=${days}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const successMessage = days === 0
          ? `All IP logs deleted (${data.deleted_count} records)`
          : data.deleted_count > 0
            ? `Successfully deleted ${data.deleted_count} IP logs older than ${days} days`
            : `No IP logs found older than ${days} days`;
        
        toast({
          title: data.deleted_count > 0 ? 'Success' : 'Info',
          description: successMessage,
          variant: data.deleted_count > 0 ? 'default' : 'default'
        });
        fetchLogs();
      } else {
        toast({
          title: 'Error',
          description: 'Cleanup failed',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error cleaning up logs:', error);
      toast({
        title: 'Error',
        description: 'Cleanup failed',
        variant: 'destructive'
      });
    }
  };

  const handleBanUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/users/ban`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          duration: banDuration,
          reason: banReason || 'Terms of service violation'
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: `User banned successfully`
        });
        setBanModalOpen(false);
        setSelectedUser(null);
        setBanReason('');
        fetchLogs();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to ban user',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error banning user:', error);
      toast({
        title: 'Error',
        description: 'Failed to ban user',
        variant: 'destructive'
      });
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/users/unban`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'User unbanned successfully'
        });
        fetchLogs();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to unban user',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error unbanning user:', error);
      toast({
        title: 'Error',
        description: 'Failed to unban user',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/users/${selectedUser.user_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'User deleted permanently'
        });
        setDeleteModalOpen(false);
        setSelectedUser(null);
        fetchLogs();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to delete user',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete user',
        variant: 'destructive'
      });
    }
  };

  const getActionTypeBadge = (actionType) => {
    const badges = {
      'user_registered': <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">User</Badge>,
      'trainer_registered': <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">Trainer</Badge>,
    };
    return badges[actionType] || <Badge>{actionType}</Badge>;
  };

  const getRoleBadge = (role) => {
    // Normalize role to lowercase for matching
    const normalizedRole = role?.toLowerCase();
    
    const badges = {
      'user': <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">User</Badge>,
      'trainer': <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">Trainer</Badge>,
      'admin': <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Admin</Badge>,
    };
    return badges[normalizedRole] || <Badge className="bg-gray-100 dark:bg-gray-700 text-gray-800">{role}</Badge>;
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold dark:text-gray-100 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Registration IP Logs
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            View registered users and trainers with their registration IP addresses (90-day retention)
          </p>
        </div>
        
        {/* Action Buttons - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button onClick={fetchLogs} variant="outline" className="w-full sm:w-auto">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => openCleanupDialog(90)} 
            variant="destructive" 
            size="sm"
            className="w-full sm:w-auto"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Cleanup Old Logs
          </Button>
          <Button 
            onClick={() => openCleanupDialog(0)} 
            variant="destructive" 
            size="sm" 
            className="w-full sm:w-auto bg-red-700 hover:bg-red-800"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete All Logs
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600 dark:text-purple-400">
                {logs.filter(l => l.action_type === 'user_registered').length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Users</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                {logs.filter(l => l.action_type === 'trainer_registered').length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Trainers</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {new Set(logs.map(l => l.ip_address)).size}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Unique IPs</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={logs}
        columns={[
          { header: 'Registration Date', accessor: (row) => row.timestamp ? new Date(row.timestamp).toLocaleString() : 'N/A' },
          { header: 'Name', accessor: (row) => row.user_name || 'N/A' },
          { header: 'Email', accessor: (row) => row.metadata?.email || row.user_email || 'N/A' },
          { header: 'Role', accessor: (row) => row.user_role || 'N/A' },
          { header: 'IP Address', accessor: (row) => row.ip_address || 'N/A' },
          { header: 'City', accessor: (row) => row.city || 'N/A' },
          { header: 'Country', accessor: (row) => row.country || 'N/A' }
        ]}
        filename="ip-logs"
      />

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent IP Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">No logs found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b dark:border-gray-700">
                    <th className="text-left p-3 font-semibold">Registration Date</th>
                    <th className="text-left p-3 font-semibold">Name</th>
                    <th className="text-left p-3 font-semibold">Email</th>
                    <th className="text-left p-3 font-semibold">Role</th>
                    <th className="text-left p-3 font-semibold">IP Address</th>
                    <th className="text-right p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => (
                    <tr 
                      key={index} 
                      id={`user-row-${log.user_id}`}
                      className={`border-b dark:border-gray-700 transition-all duration-500 ${
                        highlightUserId === log.user_id
                          ? 'bg-gradient-to-r from-yellow-100 via-yellow-50 to-yellow-100 dark:from-yellow-900/30 dark:via-yellow-800/20 dark:to-yellow-900/30 animate-pulse-once'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <td className="p-3 text-gray-600 dark:text-gray-400">
                        {new Date(log.timestamp).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="dark:text-gray-200 font-medium">{log.user_name}</span>
                          {log.is_deleted && (
                            <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 text-xs">
                              Account Deleted
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {log.metadata?.email || 'N/A'}
                        </span>
                      </td>
                      <td className="p-3">{getRoleBadge(log.user_role)}</td>
                      <td className="p-3">
                        <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs font-mono">
                          {log.ip_address}
                        </code>
                      </td>
                      <td className="p-3">
                        {log.user_role !== 'admin' ? (
                          <div className="flex items-center justify-end gap-2">
                            {log.banned_until ? (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleUnbanUser(log.user_id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Unban
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  setSelectedUser(log);
                                  setBanModalOpen(true);
                                }}
                                className="bg-orange-600 hover:bg-orange-700"
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Ban
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedUser(log);
                                setDeleteModalOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        ) : (
                          <div className="text-right">
                            <Badge className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                              Protected
                            </Badge>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            variant="outline"
            size="sm"
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <Button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            variant="outline"
            size="sm"
          >
            Next
          </Button>
        </div>
      )}

      {/* Ban Modal */}
      {banModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold dark:text-gray-100 flex items-center gap-2">
                <Ban className="w-5 h-5 text-orange-600" />
                Ban User
              </h3>
              <button onClick={() => setBanModalOpen(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <strong>User:</strong> {selectedUser.user_name} ({selectedUser.metadata?.email})
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Role:</strong> {selectedUser.user_role}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ban Duration
                </label>
                <select
                  value={banDuration}
                  onChange={(e) => setBanDuration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border dark:border-gray-700-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="1_day">1 Day</option>
                  <option value="1_week">1 Week</option>
                  <option value="1_month">1 Month</option>
                  <option value="permanent">Permanent (Unlimited)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Terms of service violation..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border dark:border-gray-700-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleBanUser}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  Ban User
                </Button>
                <Button
                  onClick={() => setBanModalOpen(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete User Permanently
              </h3>
              <button onClick={() => setDeleteModalOpen(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border dark:border-gray-700-red-800 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                  ⚠️ Warning: This action cannot be undone!
                </p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  This will permanently delete the user and all associated data including:
                </p>
                <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside mt-2">
                  <li>User account</li>
                  <li>Trainer profile (if applicable)</li>
                  <li>All bookings</li>
                  <li>Message history</li>
                </ul>
              </div>

              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <strong>User:</strong> {selectedUser.user_name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Email:</strong> {selectedUser.metadata?.email}
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleDeleteUser}
                  variant="destructive"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Permanently
                </Button>
                <Button
                  onClick={() => setDeleteModalOpen(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for highlight animation */}
      <style jsx="true">{`
        @keyframes pulse-once {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }
        
        .animate-pulse-once {
          animation: pulse-once 1s ease-in-out 2;
        }
        
        /* Smooth fade out effect */
        tr {
          transition: background-color 0.8s ease-out, opacity 0.8s ease-out;
        }
      `}</style>

      {/* Cleanup Confirmation Dialog */}
      <AlertDialog open={cleanupDialogOpen} onOpenChange={setCleanupDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {cleanupDays === 0 ? '⚠️ Delete All Logs?' : 'Cleanup Old Logs?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {cleanupDays === 0 
                ? 'Are you sure you want to DELETE ALL IP logs? This action cannot be undone!'
                : `Are you sure you want to delete IP logs older than ${cleanupDays} days?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCleanup}
              className={cleanupDays === 0 ? 'bg-red-700 hover:bg-red-800' : 'bg-red-600 hover:bg-red-700'}
            >
              {cleanupDays === 0 ? 'Yes, Delete All' : 'Yes, Cleanup'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IPLogsManager;
